import * as THREE from 'three';
import type { ShotKind } from './visuals';

/**
 * How a shot's landing is drawn, so a hit and a miss read apart at game zoom
 * (a tile is 1 world unit from centre to corner). Client-only: the sim decides
 * whether a shot hits (`Shot.hit`); this says how big and bright each kind's
 * impact is, plus the shared pooled geometry, materials and life curves the
 * shots layer draws them with (instanced, nothing allocated per impact).
 *
 * A miss kicks up a dust puff and dirt clods where it lands, and a bullet,
 * shell or beam glancing off the ground throws a few ricochet sparks. A hit
 * flashes white-hot on the target, bursts, throws sparks, and tints the
 * target itself for a moment (`TARGET_FLASH`).
 */

export interface MissVisual {
  /** Radius of the dust puff where it lands. */
  dust: number;
  /** How far the puff drifts up over its life. */
  dustRise: number;
  /** Dirt clods thrown up (the first N of `DIRT_CLODS`) and their size. */
  clods: number;
  clodSize: number;
  /** Ricochet sparks (the first N of `RICOCHET_DIRS`; 0: none), their length and how far they fly. */
  sparks: number;
  sparkLength: number;
  sparkReach: number;
}

export interface HitVisual {
  /** Radius of the white-hot flash at the point of impact. */
  flash: number;
  /** Radius of the fiery burst swelling behind it. */
  burst: number;
  /** Sparks thrown off the target (the first N of `RICOCHET_DIRS`), their length and reach. */
  sparks: number;
  sparkLength: number;
  sparkReach: number;
}

export interface ImpactVisual {
  miss: MissVisual;
  hit: HitVisual;
  /** Colour of this kind's sparks (hit and miss). */
  sparkColor: string;
}

/** Shot kind → how its impacts look: light arms small and quick, shells and missiles big. */
export const IMPACT_VISUALS: Record<ShotKind, ImpactVisual> = {
  tracer: {
    miss: {
      dust: 0.16,
      dustRise: 0.12,
      clods: 3,
      clodSize: 0.035,
      sparks: 3,
      sparkLength: 0.13,
      sparkReach: 0.3,
    },
    hit: { flash: 0.14, burst: 0.16, sparks: 3, sparkLength: 0.12, sparkReach: 0.26 },
    sparkColor: '#ffe7a3',
  },
  // An arrow just sticks in the dirt: a puff, no sparks.
  arrow: {
    miss: {
      dust: 0.12,
      dustRise: 0.08,
      clods: 2,
      clodSize: 0.03,
      sparks: 0,
      sparkLength: 0,
      sparkReach: 0,
    },
    hit: { flash: 0.1, burst: 0.12, sparks: 2, sparkLength: 0.08, sparkReach: 0.18 },
    sparkColor: '#fff1cc',
  },
  shell: {
    miss: {
      dust: 0.42,
      dustRise: 0.3,
      clods: 6,
      clodSize: 0.07,
      sparks: 5,
      sparkLength: 0.22,
      sparkReach: 0.5,
    },
    hit: { flash: 0.3, burst: 0.42, sparks: 6, sparkLength: 0.22, sparkReach: 0.5 },
    sparkColor: '#ffc46b',
  },
  missile: {
    miss: {
      dust: 0.5,
      dustRise: 0.36,
      clods: 6,
      clodSize: 0.08,
      sparks: 6,
      sparkLength: 0.26,
      sparkReach: 0.6,
    },
    hit: { flash: 0.36, burst: 0.52, sparks: 6, sparkLength: 0.26, sparkReach: 0.6 },
    sparkColor: '#ffb04d',
  },
  // A beam scorches the ground: a small puff and cyan sparks.
  beam: {
    miss: {
      dust: 0.18,
      dustRise: 0.14,
      clods: 2,
      clodSize: 0.04,
      sparks: 4,
      sparkLength: 0.15,
      sparkReach: 0.36,
    },
    hit: { flash: 0.2, burst: 0.2, sparks: 4, sparkLength: 0.15, sparkReach: 0.34 },
    sparkColor: '#9ff2ff',
  },
};

/** Seconds of real time each impact effect lasts. */
export const DUST_LIFE = 0.9;
export const CLOD_LIFE = 0.55;
export const SPARK_LIFE = 0.3;
export const HIT_FLASH_LIFE = 0.12;
export const HIT_BURST_LIFE = 0.35;

/** Dirt clods thrown up by a miss: offsets (× the dust radius) and upward speed (× clod size). */
export const DIRT_CLODS: readonly { x: number; z: number; up: number }[] = [
  { x: 0, z: 0, up: 9 },
  { x: 0.7, z: 0.3, up: 6 },
  { x: -0.55, z: 0.55, up: 7 },
  { x: -0.15, z: -0.7, up: 5 },
  { x: 0.45, z: -0.5, up: 6.5 },
  { x: -0.7, z: -0.2, up: 5.5 },
];

/**
 * Directions sparks fly from an impact: unit vectors fanned round the compass
 * at a steep-to-shallow climb. `sparkDirection` spins the fan per impact so
 * neighbouring impacts don't spark identically.
 */
export const RICOCHET_DIRS: readonly THREE.Vector3[] = [
  [0, 0.9],
  [2.2, 0.55],
  [4.1, 0.75],
  [1.1, 0.35],
  [3.2, 0.45],
  [5.2, 0.6],
].map(
  ([azimuth, climb]) =>
    new THREE.Vector3(
      Math.cos(azimuth) * Math.cos(climb),
      Math.sin(climb),
      Math.sin(azimuth) * Math.cos(climb)
    )
);

/** How far a spark falls over its life, world units. */
const SPARK_DROP = 0.18;

/** Spark `i`'s direction turned `spin` radians about the vertical. */
export function sparkDirection(i: number, spin: number, out: THREE.Vector3): THREE.Vector3 {
  const dir = RICOCHET_DIRS[i % RICOCHET_DIRS.length];
  const c = Math.cos(spin);
  const s = Math.sin(spin);
  return out.set(dir.x * c - dir.z * s, dir.y, dir.x * s + dir.z * c);
}

/**
 * Where a spark flying `dir` from `origin` is at `t` (0–1) of its life: fast
 * out, slowing, then dropping. Returns its length scale (1 → 0).
 */
export function sparkAt(
  origin: THREE.Vector3,
  dir: THREE.Vector3,
  reach: number,
  t: number,
  out: THREE.Vector3
): number {
  const travel = reach * (1 - (1 - t) * (1 - t));
  out.copy(origin).addScaledVector(dir, travel);
  out.y -= SPARK_DROP * t * t;
  return 1 - t;
}

/** A dust puff's radius scale at `t`: billows out fast, then settles and thins away. */
export function dustScale(t: number): number {
  return (0.35 + 0.65 * (1 - (1 - t) ** 3)) * (1 - t ** 4);
}

/** A clod's height at `t` (× its upward speed; peaks at a quarter of it midway): thrown up and falling back. */
export function clodHeight(t: number): number {
  return t * (1 - t);
}

/** The hit flash's radius scale at `t`: at full size at once, shrinking away. */
export function flashScale(t: number): number {
  return 1 - 0.4 * t;
}

/** A hit burst's radius scale at `t`: swells fast, then shrinks away. */
export function burstScale(t: number): number {
  return Math.sin(Math.PI * Math.sqrt(t));
}

/**
 * Brightness (1 → 0) of an additive effect at `t`, to scale its instance
 * colour by (`fadeColor`): an additive colour faded to black vanishes.
 */
export function fade(t: number): number {
  const left = Math.max(0, 1 - t);
  return left * left;
}

/** `base` × `amount` into `out` (for instance colours on additive materials). */
export function fadeColor(base: THREE.Color, amount: number, out: THREE.Color): THREE.Color {
  return out.copy(base).multiplyScalar(amount);
}

/**
 * The flash over a hit target itself: an additive shell round the model, lit
 * for `life` seconds. Sizes are world units (unit models at `UNIT_SCALE`).
 */
export const TARGET_FLASH = {
  life: 0.18,
  unit: { radius: 0.3, y: 0.22 },
  building: { radius: 0.55, y: 0.35 },
  color: new THREE.Color('#ffd9c2'),
} as const;

function noLighting(color: string, blending: THREE.Blending): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    toneMapped: false,
    transparent: true,
    depthWrite: false,
    blending,
  });
}

/**
 * Three overlapping lumps of an icosahedron (radius about 1, resting on
 * y = 0), so a puff reads as a cloud rather than a ball.
 */
function lumps(offsets: readonly [number, number, number, number][]): THREE.BufferGeometry {
  const positions: number[] = [];
  for (const [x, y, z, r] of offsets) {
    const lump = new THREE.IcosahedronGeometry(r, 0);
    lump.translate(x, y, z);
    positions.push(...(lump.getAttribute('position').array as Float32Array));
    lump.dispose();
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/** Dust puff: a low cloud, scaled per instance by `dust` × `dustScale`. */
export const DUST_GEOMETRY = lumps([
  [0, 0.45, 0, 0.6],
  [0.45, 0.3, 0.15, 0.45],
  [-0.4, 0.32, -0.2, 0.5],
]);
export const DUST_MATERIAL = new THREE.MeshStandardMaterial({
  color: '#b89a74',
  flatShading: true,
  transparent: true,
  opacity: 0.8,
  depthWrite: false,
});

/** Dirt clod: a small rock, scaled per instance by `clodSize`. */
export const CLOD_GEOMETRY = new THREE.IcosahedronGeometry(1, 0);
export const CLOD_MATERIAL = new THREE.MeshStandardMaterial({
  color: '#6e5234',
  flatShading: true,
});

/**
 * Spark: a thin diamond along +z (point it with `Object3D.lookAt`), scaled
 * per instance to (width, width, length). White in the geometry; its colour
 * (and fade) comes from the instance colour on an additive material.
 */
export const SPARK_GEOMETRY = new THREE.OctahedronGeometry(0.5, 0);
export const SPARK_WIDTH = 0.025;
export const SPARK_MATERIAL = noLighting('#ffffff', THREE.AdditiveBlending);

/** The white-hot flash where a hit lands (instance colour fades it). */
export const FLASH_GEOMETRY = new THREE.IcosahedronGeometry(1, 1);
export const FLASH_MATERIAL = noLighting('#ffffff', THREE.AdditiveBlending);

/** The fiery burst swelling behind a hit's flash. */
export const BURST_GEOMETRY = new THREE.IcosahedronGeometry(1, 0);
export const BURST_MATERIAL = new THREE.MeshBasicMaterial({ color: '#ff9a3c', toneMapped: false });

/** The shell lighting up a hit target (`TARGET_FLASH`; instance colour fades it). */
export const TARGET_FLASH_GEOMETRY = new THREE.IcosahedronGeometry(1, 1);
export const TARGET_FLASH_MATERIAL = noLighting('#ffffff', THREE.AdditiveBlending);

/** The spark colours per kind, made once (fade them with `fadeColor`). */
export const SPARK_COLORS: Record<ShotKind, THREE.Color> = Object.fromEntries(
  Object.entries(IMPACT_VISUALS).map(([kind, visual]) => [kind, new THREE.Color(visual.sparkColor)])
) as Record<ShotKind, THREE.Color>;

/** The hit flash's colour at full brightness. */
export const FLASH_COLOR = new THREE.Color('#fff6e0');
